import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { Separator } from 'components/common/WidgetWithTitle/Separator/separator';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { useGroupByTableLogic } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard.logic';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard.module.scss';
import { useGetPivotTableProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard.provider';
import { PivotFilters } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/Filters/PivotFilters';
import { GroupedByTable } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/GroupByTable/GroupedByTable';
import { initialState } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/GroupByTable/GroupedByTable.utils';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';

export const PivotBoard = ({
    projectId,
    contractId,
    latestVersionId,
    widgetId,
    localFilters,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetViewMode = WidgetView.page,
    globalFilterData,
    title: propTitle,
}: IComponentProps) => {
    const { componentKey, id, endpoints, name: configTitle } = useConfigurationStore().activitiesPivotTableView;
    const titleToUse = propTitle || configTitle;

    const { api, ui } = useGetPivotTableProvider({
        globalFilterData,
        latestVersionId,
        contractId,
        projectId,
        getPivotTableQueryEndpoint: endpoints['getPivotTable'],
    });

    const { logic } = useGroupByTableLogic({ groupsData: ui.state.groupedByTableFlatData ?? [], ui, api });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: logic.groupedByTableColumns,
        widgetViewMode,
    });

    const endpoint = endpoints['getPivotTable'];

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: titleToUse,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getPivotTable.payload,
        widgetEndpoint: endpoint,
    });

    if (WidgetViewModeUtils.isPageView(widgetViewMode) && ui.state.isPivotTableViewSelected) {
        return (
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle
                        title={editableTitle}
                        setTitle={setEditableTitle}
                        widgetViewMode={widgetViewMode}
                        showPostfixInEditMode={true}
                        postfix={`(${logic.groupedByTableDataTree.length ?? 0})`}
                    />
                }
                titleComponents={[
                    <PivotFilters key={'PivotTableFilters'} ui={ui} />,
                    <Separator key={'separator'} className={classes.separator} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown
                            key={'ExportDropdown'}
                            title={titleToUse}
                            subTitle={subTitle}
                            handleAPICsvExport={
                                logic.handleAPICsvExportPrivilege
                                    ? logic.getExportPivotTableToCsvHandler(titleToUse, subTitle)
                                    : undefined
                            }
                        />
                    ),
                    <ColumnsManager
                        key={'PivotTableColumnsManager'}
                        onOpenModal={() => table.setModalColumnsOpen(true)}
                        onHideModal={() => table.setModalColumnsOpen(false)}
                        showModal={table.modalColumnsOpen}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        tableInstance={table.tableInstance}
                        initialState={initialState}
                        columns={logic.groupedByTableColumns}
                        onChangeOrder={table.setColumnOrder}
                        onChangeVisibility={table.setColumnVisibility}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={titleToUse}
                            shareLink={route}
                        />
                    ),
                ]}
            >
                <TableContainer>
                    <GroupedByTable
                        TableData={logic.groupedByTableDataTree}
                        table={table}
                        isLoading={ui.state.isGroupByTableLoading || api.exportPivotTableToCsv.instance.isLoading}
                        tableColumns={logic.groupedByTableColumns}
                    />
                </TableContainer>
            </WidgetWithTitle>
        );
    } else {
        // should display pivot table only in project activities page and when Pivot selected
        return null;
    }
};
